import type { ReportBlock } from '@/lib/types/report'
import { FigureFrame } from '../primitives/FigureFrame'
import { REPORT_CATEGORICAL, REPORT_NEUTRAL } from '@/lib/report/tokens'

interface Slice {
  name: string
  pct: number
  score: number
}

interface Data {
  unit: string | null
  slices: Slice[]
  other: Slice | null
  center_value: string
  center_label: string
  caption: string
  source: string
}

const R = 70 // ring radius in the 200×200 viewBox
const RING = 26 // thin ring — the mark, not a slab

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)]
}

function arcPath(a0: number, a1: number): string {
  const [x0, y0] = polar(100, 100, R, a0)
  const [x1, y1] = polar(100, 100, R, a1)
  const large = a1 - a0 > 180 ? 1 : 0
  return `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1}`
}

export function CompositionDonut({ block }: { block: ReportBlock }) {
  const d = block.data as unknown as Data
  const colored: (Slice & { color: string })[] = [
    ...d.slices.map((s, i) => ({ ...s, color: REPORT_CATEGORICAL[i] })),
    ...(d.other ? [{ ...d.other, color: REPORT_NEUTRAL }] : []),
  ]
  if (colored.length === 0) return null

  // 2px surface gap expressed as an angular gap at radius R
  const gapDeg = (2 / (2 * Math.PI * R)) * 360
  const [value, ...unitParts] = d.center_value.split(' ')
  const unit = unitParts.join(' ')

  let angle = -90
  const arcs = colored.map((s) => {
    const sweep = (s.pct / 100) * 360
    const inset = Math.min(gapDeg, sweep / 2) / 2
    const path = { d: arcPath(angle + inset, angle + sweep - inset), color: s.color, name: s.name }
    angle += sweep
    return path
  })

  return (
    <FigureFrame caption={d.caption} source={d.source}>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <svg viewBox="0 0 200 200" width={190} height={190} role="img" aria-label={d.caption}>
          {colored.length === 1 ? (
            <circle cx={100} cy={100} r={R} fill="none" stroke={arcs[0].color} strokeWidth={RING} data-slice={arcs[0].name} />
          ) : (
            arcs.map((a, i) => (
              <path key={`${a.name}-${i}`} d={a.d} fill="none" stroke={a.color} strokeWidth={RING} data-slice={a.name} />
            ))
          )}
          {/* proportional figures on purpose (standalone value, not a column) */}
          <text x={100} y={92} textAnchor="middle" className="fill-foreground" fontSize={22} fontWeight={600}>
            {value}
          </text>
          {unit && (
            <text x={100} y={110} textAnchor="middle" className="fill-muted-foreground" fontSize={11}>
              {unit}
            </text>
          )}
          <text x={100} y={126} textAnchor="middle" className="fill-muted-foreground" fontSize={10}>
            {d.center_label}
          </text>
        </svg>
        <ul className="min-w-[12rem] flex-1 space-y-1.5 text-sm">
          {colored.map((s, i) => (
            <li key={`${s.name}-${i}`} className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: s.color }} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-foreground" title={s.name}>{s.name}</span>
              <span className="tabular-nums text-muted-foreground">{s.pct.toFixed(1)}%</span>
            </li>
          ))}
        </ul>
      </div>
    </FigureFrame>
  )
}
